import React from 'react';

import { useForm } from 'hooks/useForm';
import { TChangePasswordFormValidation } from 'types/state';
import { useServices } from 'hooks/useServices';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { DONE_TIMEOUT_DURATION } from 'utils/constants';
import { useAsyncActionViewScreen } from 'hooks/useAsyncViewScreen';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { PassworMatchLoginError, WrongPasswordError } from 'utils/errors';
import { createChangePasswordFormValidator } from 'utils/createChangePasswordFormValidator';
import { DashboardPasswordEditorControls } from 'containers/DashboardPasswordEditor/DashboardPasswordEditorControls';
import { testIdMap } from 'utils/testIdMap';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';
import { TextComponent } from 'design/text/textComponents';
import { CustomFieldsetComponent } from 'design/customFieldset/customFieldsetComponents';
import { PopupComponent } from 'design/popup/popupComponents';
import { CustomFormComponent } from 'design/customForm/customFormComponents';
import { CustomInputComponent } from 'design/customInput/customInputComponents';
import { useServiceState } from 'hooks/useServiceState';
import { useUserRoles } from 'hooks/useUserRole';

type TProps = {
  accountId?: number;
  onClose(): void;
  onSubmitSucceeded(): void;
};

const initialValues = {
  actualPassword: '',
  nextPassword: '',
  nextPasswordRepeat: '',
};

export const DashboardPasswordEditor = (props: TProps) => {
  const { accountId, onClose, onSubmitSucceeded } = props;

  const requireActualPassword = !accountId;

  // Подключаются роли пользователя.
  const userRoles = useUserRoles();

  // Подключается словарь интернационализации.
  const intl = useIntlDictionary();

  // Подключается сервис на обновление пользовательского пароля.
  const {
    updateUserPassword: updateUserPasswordService,
    updateClientPassword: updateClientPasswordService,
  } = useServices();

  // Состояние сервисной валидации.
  const [serviceValidation, setServiceValidation] = React.useState<
    TChangePasswordFormValidation
  >({});

  // Валидатор значений формы.
  const validator = React.useMemo(
    () =>
      createChangePasswordFormValidator(intl, {
        requireActualPassword,
      }),
    [intl, requireActualPassword]
  );

  // Состояние и методы формы.
  const { values, validation: localValidation, validate, onChange } = useForm({
    initialValues,
    validator,
  });

  // Состояние и метод отправки запроса на изменение пароля.
  const [updateOwnPasswordState, updateOwnPassword] = useServiceState(
    updateUserPasswordService
  );

  const [updatePartnerPasswordState, updatePartnerPassword] = useServiceState(
    updateClientPasswordService
  );

  const updatePasswordState = accountId
    ? updatePartnerPasswordState
    : updateOwnPasswordState;

  // Эффект следит за состоянием запроса на обновление
  // пароля и обрабатывает исключения.
  React.useEffect(() => {
    if (updatePasswordState && updatePasswordState.rejectedWith) {
      const { rejectedWith: error } = updatePasswordState;

      if (error instanceof PassworMatchLoginError) {
        setServiceValidation({
          nextPassword: intl.inputValidationMatchWithEmail,
        });
      }

      if (error instanceof WrongPasswordError) {
        setServiceValidation({
          actualPassword: intl.inputValidationWrongPassword,
        });
      }
    }
  }, [updatePasswordState, values, intl]);

  // Эффект следит за состоянием запроса и сбрасывает
  // сервисную валидацию в случае отправки нового запроса.
  React.useEffect(() => {
    if (updatePasswordState && updatePasswordState.pending) {
      setServiceValidation({});
    }
  }, [updatePasswordState]);

  // Эффект следит за состоянием запроса на
  // изменение пароля и в случае успеха вызывает
  // соответствующий колбэк.
  React.useEffect(() => {
    if (updatePasswordState && updatePasswordState.resolved) {
      return setCustomTimeout(() => {
        onSubmitSucceeded();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [updatePasswordState, values, onSubmitSucceeded]);

  // Хэндлер сабмита, проверяет значения формы
  // на валидность, после чего отправляет запрос
  // на изменение пароля.
  const onSubmit = React.useCallback(() => {
    validate(() => {
      if (accountId) {
        updatePartnerPassword({
          clientId: accountId,
          nextPassword: values.nextPassword,
        });
      } else {
        updateOwnPassword({
          nextPassword: values.nextPassword,
          actualPassword: values.actualPassword,
          userRoles,
        });
      }
    });
  }, [
    validate,
    accountId,
    updatePartnerPassword,
    values.nextPassword,
    values.actualPassword,
    updateOwnPassword,
    userRoles,
  ]);

  // Актуальный экран
  const screen = useAsyncActionViewScreen(updatePasswordState);

  // Объединенная валидация
  const validation = {
    ...serviceValidation,
    ...localValidation,
  };

  return (
    <PopupComponent onOutsideClick={onClose}>
      <CustomFormComponent onSubmit={onSubmit}>
        <CustomFieldsetComponent disabled={screen !== 'ACTIVE'}>
          {requireActualPassword && (
            <CustomInputComponent
              autoFocus
              value={values.actualPassword}
              errorMessage={validation.actualPassword}
              type="password"
              name="actualPassword"
              placeholder={intl.actualPasswordInputPlaceholder}
              onChange={onChange}
              inputTestId={
                testIdMap.DASHBOARD_PASSWORD_EDITOR__ACTUAL_PASSWORD_INPUT
              }
              errorTestId={
                testIdMap.DASHBOARD_PASSWORD_EDITOR__ACTUAL_PASSWORD_VALIDATION
              }
            />
          )}
          <CustomInputComponent
            autoFocus={!requireActualPassword}
            value={values.nextPassword}
            errorMessage={validation.nextPassword}
            type="password"
            name="nextPassword"
            placeholder={intl.nextPasswordInputPlaceholder}
            onChange={onChange}
            inputTestId={
              testIdMap.DASHBOARD_PASSWORD_EDITOR__NEXT_PASSWORD_INPUT
            }
            errorTestId={
              testIdMap.DASHBOARD_PASSWORD_EDITOR__NEXT_PASSWORD_VALIDATION
            }
          />
          <CustomInputComponent
            value={values.nextPasswordRepeat}
            errorMessage={validation.nextPasswordRepeat}
            type="password"
            name="nextPasswordRepeat"
            placeholder={intl.nextPasswordRepeatInputPlaceholder}
            onChange={onChange}
            inputTestId={
              testIdMap.DASHBOARD_PASSWORD_EDITOR__NEXT_PASSWORD_REPEAT_INPUT
            }
            errorTestId={
              testIdMap.DASHBOARD_PASSWORD_EDITOR__NEXT_PASSWORD_REPEAT_VALIDATION
            }
          />
        </CustomFieldsetComponent>
        <DashboardPasswordEditorControls>
          <ClickControlComponent
            disabled={screen === 'PENDING' || screen === 'DONE'}
            onClick={onClose}
          >
            <TextComponent color="secondary">{intl.cancelButton}</TextComponent>
          </ClickControlComponent>
          <ClickControlComponent
            disabled={screen === 'PENDING' || screen === 'DONE'}
            submit
          >
            <TextComponent color="accent">{intl.saveButton}</TextComponent>
          </ClickControlComponent>
        </DashboardPasswordEditorControls>
      </CustomFormComponent>
    </PopupComponent>
  );
};
